import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { PageHeader, DataTable, PrimaryButton } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import ExportButton from "@/components/ExportButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, PackageCheck } from "lucide-react";

export default function GRNsList() {
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ po_id: "", challan_no: "", batch_no: "", heat_no: "", manufacturer: "", vehicle_no: "", driver_name: "", remarks: "", items: [] });
  const { hasRole } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/grns").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/pos").then((r) => setPos(r.data));
  }, []);

  const openNew = (poId) => {
    const po = pos.find((p) => p.id === poId);
    if (!po) return;
    setForm({
      po_id: poId, challan_no: "", batch_no: "", heat_no: "", manufacturer: "", vehicle_no: "", driver_name: "", remarks: "",
      items: po.items.map((it, i) => ({
        po_item_index: i, material_name: it.material_name, unit: it.unit,
        ordered_qty: it.qty, previously_received: it.delivered_qty || 0, balance_qty: it.pending_qty ?? it.qty,
        received_qty: it.pending_qty ?? it.qty, accepted_qty: it.pending_qty ?? it.qty, rejected_qty: 0, rate: it.rate || 0, remarks: "",
      })),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const { data } = await api.post("/grns", form);
      toast.success(`GRN ${data.grn_no} recorded`);
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const canCreate = hasRole("Store Keeper", "Administrator");

  return (
    <div data-testid="grns-page">
      <PageHeader
        title="Goods Receipt Notes"
        subtitle="Store keepers record physical receipt of materials. Invoice details are captured separately when the memo arrives."
        actions={
          <>
            <ExportButton entity="grns" filename="grns.xlsx" />
            {canCreate && (
              <Select onValueChange={openNew}>
                <SelectTrigger className="w-72" data-testid="new-grn-select"><SelectValue placeholder="+ Record receipt against PO…" /></SelectTrigger>
                <SelectContent>
                  {pos.length === 0 && <div className="p-2 text-xs text-muted-foreground">No active POs</div>}
                  {pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number || p.draft_po_id} — {p.vendor_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </>
        }
      />
      <DataTable
        testid="grns-table"
        columns={[
          { key: "grn_no", label: "GRN No.", render: (r) => <span className="font-mono text-xs font-semibold">{r.grn_no}</span> },
          { key: "po_number", label: "PO Ref", render: (r) => <span className="font-mono text-xs">{r.po_number}</span> },
          { key: "vendor_name", label: "Vendor" },
          { key: "challan_no", label: "Challan" },
          { key: "vehicle_no", label: "Vehicle" },
          { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
          { key: "created_by_name", label: "By" },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
        ]}
        data={items}
        empty="No GRNs recorded yet."
        onRow={(r) => nav(`/grns/${r.id}`)}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Record Goods Receipt</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label className="text-xs uppercase font-semibold">Challan No</Label><Input value={form.challan_no} onChange={(e) => setForm({ ...form, challan_no: e.target.value })} data-testid="grn-challan" /></div>
            <div><Label className="text-xs uppercase font-semibold">Vehicle No</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} data-testid="grn-vehicle" /></div>
            <div><Label className="text-xs uppercase font-semibold">Driver</Label><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Batch No</Label><Input value={form.batch_no} onChange={(e) => setForm({ ...form, batch_no: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Heat No</Label><Input value={form.heat_no} onChange={(e) => setForm({ ...form, heat_no: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Manufacturer</Label><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-2">Invoice details will be captured later when the vendor memo arrives.</div>

          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Material</th>
                  <th className="text-right px-2 py-2">Ordered</th>
                  <th className="text-right px-2 py-2">Prev. Recv</th>
                  <th className="text-right px-2 py-2">Received Now</th>
                  <th className="text-right px-2 py-2">Accepted</th>
                  <th className="text-right px-2 py-2">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{it.material_name}</td>
                    <td className="px-2 py-1 text-right">{it.ordered_qty}</td>
                    <td className="px-2 py-1 text-right text-muted-foreground">{it.previously_received}</td>
                    <td className="px-1 py-1"><Input type="number" className="h-8 text-right w-24" value={it.received_qty} onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, received_qty: parseFloat(e.target.value) || 0, accepted_qty: parseFloat(e.target.value) || 0 } : x) })} data-testid={`grn-received-${i}`} /></td>
                    <td className="px-1 py-1"><Input type="number" className="h-8 text-right w-24" value={it.accepted_qty} onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, accepted_qty: parseFloat(e.target.value) || 0 } : x) })} /></td>
                    <td className="px-1 py-1"><Input type="number" className="h-8 text-right w-24" value={it.rejected_qty} onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, rejected_qty: parseFloat(e.target.value) || 0 } : x) })} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Textarea placeholder="Remarks…" rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={save} data-testid="save-grn"><PackageCheck size={14} className="mr-2" />Record GRN</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
